import React, { useState, useRef, useEffect } from "react";
import { Button } from "./button";
import { ChevronDown, Trash2, Search } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem } from "./dropdown-menu";
import { cn } from "@/lib/utils";

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  className?: string;
  showSearch?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  className,
  showSearch = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleCheckedChange = (value: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedValues, value]);
    } else {
      onChange(selectedValues.filter((v) => v !== value));
    }
  };

  // Filter options based on search query
  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()));

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Use a longer timeout to ensure DOM is fully ready
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 200);
    }
  }, [isOpen]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Handle input keydown
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    // Prevent dropdown from closing on key events
    e.stopPropagation();
  };

  // Handle manual escape key to close
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Determine width based on whether search is enabled (company filter gets larger width)
  const dropdownWidth = showSearch ? "w-[280px]" : "w-[200px]";

  return (
    <DropdownMenu
      modal={false}
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            `${dropdownWidth} justify-between rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-normal text-gray-700`,
            "flex items-center gap-4 hover:bg-gray-50/50 hover:text-gray-900 hover:border-gray-300",
            "focus:outline-none focus:ring-2 focus:ring-gray-200",
            className
          )}
        >
          <span className="truncate flex-1 text-left">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={contentRef}
        align="start"
        className={`${dropdownWidth} rounded-lg border border-gray-200 bg-white p-0`}
        style={{ overflow: "hidden" }}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
        onPointerDownOutside={(e) => {
          if (searchInputRef.current && searchInputRef.current.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
      >
        {/* Search input - only show for company filter */}
        {showSearch && (
          <div className="p-2 border-b border-gray-200" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleInputKeyDown}
                placeholder="Pesquisar..."
                className="w-full pl-8 h-8 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        <div className="thin-scrollbar" style={{ maxHeight: "240px", overflow: "auto" }}>
          <div className="p-2">
            <div className="space-y-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={(checked) => handleCheckedChange(option.value, checked)}
                    onSelect={(event) => event.preventDefault()}
                    className="rounded-md px-2 py-1.5 text-sm font-normal text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <div className="w-5 h-5 flex-shrink-0 mr-2 relative">
                      <span
                        className={cn(
                          "absolute left-0 flex h-4 w-4 items-center justify-center border rounded transition-colors",
                          selectedValues.includes(option.value)
                            ? "border-teal-600 bg-teal-600 text-white"
                            : "border-gray-300 bg-white"
                        )}
                      >
                        {selectedValues.includes(option.value) && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path
                              d="M8.5 2.5L3.5 7.5L1.5 5.5"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                    <span className="truncate">{option.label}</span>
                  </DropdownMenuCheckboxItem>
                ))
              ) : (
                <div className="text-center py-2 text-sm text-gray-500">Nenhum resultado</div>
              )}
            </div>
          </div>
        </div>
        {selectedValues.length > 0 && (
          <div className="border-t border-gray-200 p-2 bg-white">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              className="w-full text-sm text-gray-500 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-50 text-left flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4 text-gray-400" />
              <span>Limpar</span>
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
